import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'

import { DataTable, optionalColumn, selectionColumn } from '@/components/ui/data-table'
import type { ListSort } from '@/services/core/paging'
import { DateCell, MutedCell, NameCell, StatusChip } from '@/components/TableCells'
import type { ReportTemplateDetail } from '@/services/reports/reportTemplatesApi'

/** The API field each sortable column orders by when the server sorts the list. */
const SORT_FIELDS: Record<string, string> = {
  name: 'name',
  reportType: 'reportType',
  reportFormat: 'reportFormat',
  objectState: 'objectState',
  team: 'team.name',
  updateTime: 'updateTime',
}

interface ReportTemplateTableProps {
  templates: ReportTemplateDetail[]
  loading?: boolean
  emptyMessage?: string
  selection?: ReportTemplateDetail[]
  onSelectionChange?: (templates: ReportTemplateDetail[]) => void
  /** Sorts on the server — pass both for a list the server pages. */
  sort?: ListSort | null
  onSortChange?: (sort: ListSort | null) => void
}

export function ReportTemplateTable({
  templates,
  loading,
  emptyMessage,
  selection,
  onSelectionChange,
  sort,
  onSortChange,
}: ReportTemplateTableProps) {
  const { t } = useTranslation()
  const selectable = !!onSelectionChange

  const columns: ColumnDef<ReportTemplateDetail, unknown>[] = [
    ...(selectable ? [selectionColumn<ReportTemplateDetail>()] : []),
    {
      accessorKey: 'name',
      header: t('common.name'),
      meta: { minWidth: 220 },
      cell: ({ row }) => (
        <NameCell name={row.original.name} id={row.original.id} sub={row.original.description} />
      ),
    },
    {
      accessorKey: 'reportType',
      header: t('reportsModule.reportType'),
      size: 190,
      cell: ({ row }) => (
        <MutedCell
          value={t(`reportTypes.${row.original.reportType}.simple`, {
            defaultValue: row.original.reportType,
          })}
        />
      ),
    },
    {
      accessorKey: 'reportFormat',
      header: t('reportTemplates.format'),
      size: 100,
      cell: ({ row }) => <MutedCell value={row.original.reportFormat} />,
    },
    {
      accessorKey: 'objectState',
      header: t('common.status'),
      size: 110,
      cell: ({ row }) => <StatusChip state={row.original.objectState} />,
    },
    optionalColumn<ReportTemplateDetail>({
      id: 'team',
      accessorFn: (row) => row.team?.name ?? '',
      header: t('reportTemplates.team'),
      size: 160,
      cell: ({ row }) => (
        <MutedCell value={row.original.team?.name || t('reportTemplates.general')} />
      ),
    }),
    optionalColumn<ReportTemplateDetail>({
      id: 'components',
      accessorFn: (row) => row.components.length,
      header: t('reportTemplates.components'),
      size: 120,
      cell: ({ row }) => <MutedCell value={row.original.components.length} />,
    }),
    optionalColumn<ReportTemplateDetail>({
      accessorKey: 'updateTime',
      header: t('common.updated'),
      size: 120,
      cell: ({ row }) => <DateCell value={row.original.updateTime} />,
    }),
  ]

  return (
    <DataTable
      columns={columns}
      data={templates}
      loading={loading}
      emptyMessage={emptyMessage ?? t('reportTemplates.noTemplatesFound')}
      columnsKey="reportTemplates"
      rowHref={(row) => `/reports/templates/${row.id}`}
      {...(selectable ? { selection, onSelectionChange } : {})}
      serverSort={
        onSortChange && { sort: sort ?? null, onChange: onSortChange, fields: SORT_FIELDS }
      }
    />
  )
}
